import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { pageKeyForPath } from "@/lib/pageVisibilityMap";
import { toast } from "sonner";

/**
 * Reads the `page_visibility` admin table and, when the current
 * route belongs to a page an admin has switched off, redirects
 * non-admin visitors to the home page. Admins can always reach
 * hidden pages so they can preview them before re-enabling.
 */
export default function PageVisibilityGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const { data: hiddenKeys } = useQuery({
    queryKey: ["page-visibility-hidden-keys"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_visibility")
        .select("page_key, is_visible");
      if (error) throw error;
      return new Set((data || []).filter((r: any) => r.is_visible === false).map((r: any) => r.page_key as string));
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!hiddenKeys || isAdmin) return;
    const key = pageKeyForPath(location.pathname);
    if (key && hiddenKeys.has(key)) {
      toast.info("This page is temporarily unavailable.");
      navigate("/", { replace: true });
    }
  }, [location.pathname, hiddenKeys, isAdmin, navigate]);

  return null;
}